import React, { useState, useRef, useEffect, useCallback } from 'react';
import { arrayOf, bool, func, number, object, oneOfType, shape, string, } from 'prop-types';
import Select, { components } from 'react-select';
import setClass from '../../common/set-class';
import useOutsideClick from '../../hooks/use-outside-click';
import Dropdown from './dropdown';
import DropdownIndicator from './dropdown-indicator';
import Option from './option';
const MenuList = (props) => {
    const { children } = props;
    return (React.createElement("div", { className: "select-multi-search__menu-list" },
        React.createElement(components.MenuList, { ...props }, children)));
};
const AsyncMultiSelectSearch = (props) => {
    const { className, compact, auto, disabled, extra, isInitialOpen, onChange, onClose, noOptionsMessage, loadOptions, placeholder, searchPlaceholder, style, testId, values, name, } = props;
    const [isOpen, setIsOpen] = useState(isInitialOpen);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const wrapperRef = useRef(null);
    const fetchOptions = useCallback(async () => {
        if (inputValue === '') {
            setOptions([]);
        }
        else {
            // Load new options and return an empty array in case of error
            const newOptions = await loadOptions(inputValue).catch(() => []);
            setOptions(newOptions);
        }
        setIsLoading(false);
    }, [inputValue]);
    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchOptions();
        }, 300);
        return () => clearTimeout(debounce);
    }, [fetchOptions]);
    // eslint-disable-next-line consistent-return
    const handleOnClose = async (isSelectOpen) => {
        if (isSelectOpen) {
            await new Promise((resolve) => {
                setInputValue('');
                onClose();
                // @ts-expect-error FIXME: Expected 1 arguments, but got 0. Did you forget to... Remove this comment to see the full error message
                resolve();
            });
            return setIsOpen(false);
        }
    };
    useOutsideClick(wrapperRef, (e) => {
        if (!e.composedPath().includes(wrapperRef?.current)) {
            handleOnClose(isOpen);
        }
    }, 
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'any' is not assignable to type 'never'.
    [isOpen]);
    const toggleOpen = () => {
        if (!disabled) {
            handleOnClose(isOpen);
            setIsOpen((prevToggleVal) => !prevToggleVal);
        }
    };
    const onInputChange = async (value, { action }) => {
        if (action === 'input-change') {
            setIsLoading(true);
            setOptions([]);
            setInputValue(value);
        }
    };
    const onSelectChange = (selectedValues, actionObj) => {
        onChange(selectedValues, actionObj, { inputValue, isOpen });
    };
    const onRemoveValue = (optionRemove, allValues) => {
        const valuesChange = allValues.filter(({ value }) => value !== optionRemove.value);
        onChange(valuesChange, {
            action: 'deselect-option',
            option: optionRemove,
        }, { inputValue, isOpen });
    };
    return (React.createElement("div", { className: setClass([
            'b-async-select-multi__wrapper',
            className,
            compact && 'b-select--max-width',
            auto && '--auto',
        ]), ref: wrapperRef, "data-testid": testId },
        React.createElement(Dropdown, { disabled: disabled, isOpen: isOpen, onDropdownClose: toggleOpen, onRemoveValue: onRemoveValue, placeholder: placeholder, toggleOpen: toggleOpen, values: values },
            React.createElement(Select, { className: "b-async-select-multi-menu", classNamePrefix: "b-select", inputId: `b-async-select-multi-menu-input--${name}`, instanceId: `b-async-select-multi-menu-component-instance--${name}`, name: name, components: {
                    DropdownIndicator,
                    MenuList,
                    Option,
                    ClearIndicator: null,
                    Control: components.Control,
                    LoadingIndicator: null,
                }, clearSearchValue: () => setInputValue(''), inputValue: inputValue, options: options, placeholder: searchPlaceholder, isSearchable: true, onChange: onSelectChange, onInputChange: onInputChange, value: values, noOptionsMessage: () => (React.createElement("p", { className: "b-async-select-multi-search__no-results-msg" }, noOptionsMessage)), styles: style, isDisabled: disabled, controlShouldRenderValue: false, hideSelectedOptions: false, backspaceRemovesValue: false, isMulti: true, menuIsOpen: true, autoFocus: true, setIsOpen: setIsOpen, isLoading: isLoading, ...extra }))));
};
AsyncMultiSelectSearch.propTypes = {
    className: string,
    compact: bool,
    auto: bool,
    disabled: bool,
    extra: object,
    isInitialOpen: bool,
    onChange: func,
    onClose: func,
    noOptionsMessage: string,
    loadOptions: func,
    placeholder: string,
    searchPlaceholder: string,
    style: object,
    testId: string,
    values: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    name: string,
};
AsyncMultiSelectSearch.defaultProps = {
    className: null,
    compact: false,
    auto: false,
    disabled: false,
    extra: {},
    isInitialOpen: false,
    onChange: () => { },
    onClose: () => { },
    noOptionsMessage: 'No results found',
    loadOptions: () => { },
    placeholder: 'Search Options',
    searchPlaceholder: 'Search Options',
    style: {},
    testId: undefined,
    values: [],
    name: 'default',
};
export default AsyncMultiSelectSearch;
